/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { When } from "react-if";
import { useParams } from "react-router-dom";
import { Spacer, theme } from "@nordcloud/gnui";
import { CostBox } from "~/components/CostBox";
import { useCurrency } from "~/hooks";
import { useDisclosure } from "~/hooks/useDisclosure/useDisclosure";
import { isNotEmpty } from "~/tools";
import { AccountCredentialsUpdateSidebar } from "~/views/accounts/AccountDetails/AccountCredentialsUpdateSidebar/AccountCredentialsUpdateSidebar";
import { ChargeTypeCostsBar } from "../components/ChargeTypeCostsBar";
import { Details } from "./Details";
import { DetailsForm } from "./Details/DetailsForm";
import { DetailsHeader } from "./Details/DetailsHeader";
import { Header } from "./Header";
import { useCloudAccountDetails } from "./useCloudAccountDetails";

export function CloudAccountDetailsPage() {
  const {
    open: openCredentialsUpdateSteps,
    close: closeCredentialsUpdateSteps,
    isOpen: showCredentialsUpdateSteps,
  } = useDisclosure();

  const { nid } = useParams<RouteParams>();

  const {
    account,
    tags,
    accountName,
    provider,
    providerType,
    ownerData,
    contactPeople,
    accountDescription,
    showUpdateCredentials,
    cloudAccountCache,
    chargeTypeCosts,
    providerId,
    accountSavingsCosts,
    accountSpends,
  } = useCloudAccountDetails(nid);

  const { currency } = useCurrency();

  return (
    <>
      <Header
        tags={tags}
        accountName={accountName}
        account={account}
        openCredentialsUpdateSteps={openCredentialsUpdateSteps}
        showUpdateCredentials={showUpdateCredentials}
      />
      <Details>
        <div css={{ width: "100%" }}>
          <DetailsHeader
            accountName={accountName}
            provider={provider}
            providerType={providerType}
          />
          <DetailsForm
            accountInternalId={nid}
            accountId={providerId}
            accountOwner={ownerData}
            description={accountDescription}
            cloudAccountCache={cloudAccountCache}
            contactsNames={contactPeople
              .map((contactPerson) => contactPerson.name)
              .join(", ")}
          />
        </div>
        <div css={{ width: "70%", height: "18.5rem" }}>
          <CostBox
            numericalValues={{
              forecast: accountSpends.currentMonthForecastValue,
              currentMonth: accountSpends.currentMonthValue,
              previousMonth: accountSpends.previousMonthValue,
              currency,
              budget: 0,
              cloudWasteCost: accountSavingsCosts.cost,
              co2e: accountSpends.currentMonthCo2e,
              co2eSavings: accountSavingsCosts.co2e,
              originalCost: 0,
              reservedInstanceBenefit: 0,
            }}
          />
        </div>
      </Details>
      <When condition={isNotEmpty(chargeTypeCosts)}>
        <Spacer height={theme.spacing.spacing04} />
        <ChargeTypeCostsBar chargeTypeCosts={chargeTypeCosts} />
      </When>
      {showUpdateCredentials && (
        <AccountCredentialsUpdateSidebar
          account={account}
          isOpen={showCredentialsUpdateSteps}
          close={closeCredentialsUpdateSteps}
        />
      )}
    </>
  );
}

type RouteParams = {
  nid: string;
};
